<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { dateToStr } from 'scripts/utils/date_utils';
  import type {
    TypeTableConfig,
    TableStatus,
    UpdateItem,
    StandardResult,
    ChoiceDisplayMap,
  } from 'scripts/utils/types';
  import {
    downloadCSV,
    getChoiceDisplay,
    makeInitialTableStatusRow,
  } from 'scripts/utils/CsvExportImport';
  import FileInput from './input/FileInput.svelte';
  import { makeObjectStr } from 'scripts/utils/utils';

  export let items: any[] = [];
  export let tableConfig: TypeTableConfig;
  export let choiceDisplayMap: ChoiceDisplayMap = {};
  // itemsのプライマリキー。一括更新機能を有効にする場合のみ設定する
  export let primaryKey: string = undefined;
  export let displayUpdateButton: boolean = undefined;
  export let tableStatus: TableStatus = {};
  export let rowDeletable = false;
  // 行選択チェックボックス。true にすると先頭列にチェックボックスが表示される。
  // 選択された items の配列は selectedItems でバインド可能。
  export let rowSelectable = false;
  export let selectedItems: any[] = [];
  // 後方互換のため宣言を残すが使用しない（クライアント検索廃止）
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // export let searchable = true;

  // CSV
  export let csvName = 'table.csv';
  export let csvDownload = false;
  export let csvUpload = false;
  export let csvFields: string[] = [];
  let clickFileInput: () => void;
  let csvMenuOpen = false;

  // 表示制御
  export let displayTableHeader = true;
  export let displayPagination = true;
  export let tableName = '';
  export let coloredRow: any[] = [];
  export let rowColor = '#e9e9e9';

  // ページネーション
  // 頻繁な Fetch を防ぐためデフォルト 50 件
  export const rowsSelect = [25, 50, 100, 200];
  export let rowsPerPage = 50;
  let currentPage = 0;

  // サーバーサイドページネーション（DRF対応）
  // serverPagination=true の場合、items は現在ページ分のみ渡す。
  // totalCount / nextUrl / previousUrl は DRF レスポンスの count / next / previous を渡す。
  // CSV ダウンロードは親コンポーネントが on:csvExport を受け取り全件取得して処理する。
  export let serverPagination = false;
  export let totalCount = 0;
  export let nextUrl: string | null = null;
  export let previousUrl: string | null = null;

  let bulkUpdateFields: string[] = [];
  let bulkUpdateItems: UpdateItem[] = [];

  // ソート状態
  let sortKey = '';
  let sortAsc = true;

  const dispatch = createEventDispatcher();

  // 表示列を事前計算。行ループ内で hidden チェックしないためパフォーマンス改善。
  $: visibleColumns = Object.entries(tableConfig).filter(([_, v]) => !v.hidden);

  // 一括更新フィールドを tableConfig から収集
  $: if (primaryKey) {
    bulkUpdateFields = Object.entries(tableConfig)
      .filter(([_, v]) => v.editable)
      .map(([k]) => k);
    if (displayUpdateButton === undefined)
      displayUpdateButton = bulkUpdateFields.length > 0;
  }

  // ページネーション計算
  $: totalItems = serverPagination ? totalCount : items.length;
  $: lastPage = Math.max(Math.ceil(totalItems / rowsPerPage) - 1, 0);
  $: if (currentPage > lastPage) currentPage = lastPage;
  $: start = currentPage * rowsPerPage;
  $: end = Math.min(start + rowsPerPage, totalItems);
  // サーバーページネーション時は items がすでに1ページ分なのでそのまま使う
  $: slice = serverPagination ? items : items.slice(start, end);

  const trClick = (item: any) => dispatch('trClick', { item });

  const bulkUpdateClick = () => {
    bulkUpdateItems = [];
    if (primaryKey) {
      Object.entries(tableStatus).forEach(([pk, rowVal]) => {
        const updateData = items.find((item) => item[primaryKey] == pk);
        bulkUpdateFields.forEach((fieldName) => {
          updateData[fieldName] =
            rowVal[fieldName].value !== undefined
              ? rowVal[fieldName].value
              : updateData[fieldName];
        });
        bulkUpdateItems.push(updateData);
      });
      dispatch('bulkUpdateClick', { items: bulkUpdateItems });
    } else {
      alert(
        '更新データが入力されていません。データを入力してから更新ボタンを押してください。',
      );
    }
  };

  const statusChange = (e: Event, item: any, fieldName: string) => {
    const target = e.target as HTMLInputElement;
    const pk = item[primaryKey];
    if (!Object.keys(tableStatus).includes(`${pk}`))
      tableStatus[pk] = makeInitialTableStatusRow(bulkUpdateFields);
    if (target.value !== '') {
      tableStatus[pk][fieldName] = { value: target.value, color: '#ffff66b2' };
    } else {
      tableStatus[pk][fieldName].value = target.value;
    }
  };

  const rowDelete = (item: any) => {
    items = items.filter((el) => el[primaryKey] !== item[primaryKey]);
    delete tableStatus[item[primaryKey]];
    tableStatus = tableStatus;
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      sortAsc = !sortAsc;
    } else {
      sortKey = key;
      sortAsc = true;
    }
    items = [...items].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];
      if (typeof aVal === 'string' && typeof bVal === 'string')
        return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortAsc
        ? Number(aVal) - Number(bVal)
        : Number(bVal) - Number(aVal);
    });
  };

  // CSV ダウンロード
  // サーバーページネーション時は現在ページのみ手元にあるため親に委譲。
  // クライアントページネーション時は全 items を直接エクスポート。
  const handleCsvDownload = () => {
    csvMenuOpen = false;
    if (serverPagination) {
      dispatch('csvExport', { csvName, csvFields });
    } else {
      downloadCSV(csvName, items, csvFields, tableConfig, choiceDisplayMap);
    }
  };

  // ページ移動。サーバーページネーション時は pageChange イベントを発火して
  // 親コンポーネントに API リクエストを委譲する。
  const goFirst = () => {
    currentPage = 0;
    if (serverPagination) dispatch('pageChange', { page: 0, rowsPerPage });
  };
  const goPrev = () => {
    if (serverPagination) {
      dispatch('pageChange', { url: previousUrl, rowsPerPage });
    } else {
      currentPage = Math.max(0, currentPage - 1);
    }
  };
  const goNext = () => {
    if (serverPagination) {
      dispatch('pageChange', { url: nextUrl, rowsPerPage });
    } else {
      currentPage = Math.min(lastPage, currentPage + 1);
    }
  };
  const goLast = () => {
    currentPage = lastPage;
    if (serverPagination)
      dispatch('pageChange', { page: lastPage, rowsPerPage });
  };

  const toggleSelectAll = (e: Event) => {
    const checked = (e.target as HTMLInputElement).checked;
    selectedItems = checked ? [...slice] : [];
  };

  const toggleSelectRow = (e: Event, item: any) => {
    const checked = (e.target as HTMLInputElement).checked;
    if (checked) {
      selectedItems = [...selectedItems, item];
    } else {
      selectedItems = selectedItems.filter((i) => i !== item);
    }
  };

  const csvValidation = (files: FileList): StandardResult => {
    const invalid = Array.from(files).some((f) => f.type !== 'text/csv');
    return {
      success: !invalid,
      message: invalid ? 'CSV形式ではないファイルが選択されています。' : '',
    };
  };
</script>

{#if tableName}
  <h5 class="fw-bold text-muted">{tableName}</h5>
{/if}

<div class="d-flex align-items-center mb-2 gap-2 flex-wrap">
  {#if displayUpdateButton}
    <button class="btn btn-secondary btn-sm fw-bold" on:click={bulkUpdateClick}>
      更新
    </button>
  {/if}

  {#if csvDownload || csvUpload}
    <div class="dropdown">
      <button
        class="btn btn-outline-secondary btn-sm dropdown-toggle"
        type="button"
        on:click={() => (csvMenuOpen = !csvMenuOpen)}
      >
        CSV
      </button>
      {#if csvMenuOpen}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div class="dropdown-backdrop" on:click={() => (csvMenuOpen = false)} />
        <ul class="dropdown-menu show">
          {#if csvDownload}
            <li>
              <button class="dropdown-item" on:click={handleCsvDownload}>
                CSVエクスポート
              </button>
            </li>
          {/if}
          {#if csvUpload}
            <li>
              <button
                class="dropdown-item"
                on:click={() => {
                  csvMenuOpen = false;
                  clickFileInput();
                }}
              >
                CSVインポート
              </button>
            </li>
            <FileInput
              acceptFile=".csv"
              bind:clickInput={clickFileInput}
              customValidation={csvValidation}
            />
          {/if}
        </ul>
      {/if}
    </div>
  {/if}
</div>

<div class="table-responsive editable-datatable-container">
  <table
    class="table table-hover table-bordered table-sm editable-datatable mb-0"
  >
    {#if displayTableHeader}
      <thead class="table-light">
        <tr>
          {#if rowSelectable}
            <th class="text-center px-1 py-1" style="width: 2rem;">
              <input
                type="checkbox"
                checked={selectedItems.length === slice.length &&
                  slice.length > 0}
                on:change={toggleSelectAll}
              />
            </th>
          {/if}
          {#each visibleColumns as [key, configVal]}
            <th
              class="text-nowrap text-center px-2 py-1"
              style="cursor: pointer; user-select: none;"
              on:click={() => handleSort(key)}
            >
              {configVal.label}{#if sortKey === key}<span class="ms-1"
                  >{sortAsc ? '▲' : '▼'}</span
                >{/if}
            </th>
          {/each}
          {#if rowDeletable}
            <th class="text-nowrap text-center px-2 py-1">削除</th>
          {/if}
        </tr>
      </thead>
    {/if}
    <tbody>
      {#each slice as item}
        <tr
          on:click={() => trClick(item)}
          style={coloredRow.includes(item) ? `background: ${rowColor};` : ''}
          class="table-row-clickable"
        >
          {#if rowSelectable}
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <td class="text-center px-1 py-0" on:click|stopPropagation>
              <input
                class="my-auto align-middle"
                type="checkbox"
                checked={selectedItems.includes(item)}
                on:change={(e) => toggleSelectRow(e, item)}
              />
            </td>
          {/if}
          {#each visibleColumns as [key, configVal]}
            {#if configVal.type === 'image'}
              <td class="text-center px-1 py-0">
                {#if item[key]}
                  <img src={item[key]} alt="thumbnail" />
                {:else}
                  <span class="text-muted small">No Image</span>
                {/if}
              </td>
            {:else if configVal.type === 'link_text'}
              <td style={configVal.style} class="px-2 py-1">
                <a href={item.detail_url}>{item[key] || ''}</a>
              </td>
            {:else if configVal.type === 'color'}
              <td class="px-2 py-1">
                <div style="background: {item[key]};">{item[key]}</div>
              </td>
            {:else if configVal.type === 'person_name'}
              <td class="text-nowrap px-2 py-1">
                <span class="furigana">{item['get_full_furigana']}</span><br />
                <span class="full_name">{item[key]}</span>
              </td>
            {:else if configVal.type === 'date'}
              <td class="text-nowrap px-2 py-1">
                {item[key] ? dateToStr(new Date(item[key])).YMDJPformat : ''}
              </td>
            {:else if configVal.type === 'datetime'}
              <td class="text-nowrap px-2 py-1">
                {item[key] ? dateToStr(new Date(item[key])).YMDhmJPformat : ''}
              </td>
            {:else if configVal.type === 'time'}
              <td class="text-nowrap px-2 py-1">
                {item[key] ? dateToStr(new Date(item[key])).hmformat : ''}
              </td>
            {:else if configVal.type === 'amount_changed'}
              <td class="text-end text-nowrap px-2 py-1">
                {#if item.slip_type === 'in'}
                  {`+${item[key].toLocaleString()}`}
                {:else}
                  {item[key] > 0
                    ? `+${item[key].toLocaleString()}`
                    : `${item[key].toLocaleString()}`}
                {/if}
              </td>
            {:else if ['object', 'field'].includes(configVal.type)}
              <td class="px-2 py-1">
                {makeObjectStr(item[key], configVal.objectKey)}
              </td>
            {:else if configVal.type === 'choices'}
              <td class="px-2 py-1">
                {getChoiceDisplay(choiceDisplayMap, key, item[key])}
              </td>
            {:else if configVal.type === 'float'}
              <td
                class="text-end px-2 py-1"
                style={tableStatus[item[primaryKey]]?.[key]
                  ? `background-color: ${tableStatus[item[primaryKey]][key].color}`
                  : ''}
              >
                {#if configVal.editable}
                  <input
                    bind:value={item[key]}
                    on:change={(e) => statusChange(e, item, key)}
                    type="number"
                    step="0.1"
                    min="0"
                    class="table-input-number"
                  />
                {:else}
                  {item[key] != null && isFinite(item[key])
                    ? item[key].toLocaleString()
                    : ''}
                {/if}
              </td>
            {:else if configVal.type === 'number'}
              <td
                class="text-end px-2 py-1"
                style={tableStatus[item[primaryKey]]?.[key]
                  ? `background-color: ${tableStatus[item[primaryKey]][key].color}`
                  : ''}
              >
                {#if configVal.editable}
                  <input
                    bind:value={item[key]}
                    on:change={(e) => statusChange(e, item, key)}
                    class="table-input-number"
                    style={configVal.inputStyle}
                    type="number"
                    step="1"
                    min="0"
                  />
                {:else}
                  {isFinite(item[key]) ? item[key].toLocaleString() : ''}
                {/if}
              </td>
            {:else if configVal.type === 'compare_num'}
              <td
                class="text-end px-2 py-1"
                style={tableStatus[item[primaryKey]]
                  ? `background-color: ${tableStatus[item[primaryKey]][key]?.color}`
                  : ''}
              >
                {#if configVal.editable}
                  <div style="padding-right: 1.2rem">{item[key]}</div>
                  <input
                    value={item[key]}
                    on:change={(e) => statusChange(e, item, key)}
                    class="table-input-text"
                    style="text-align:right; max-width: 5rem; {configVal.inputStyle ??
                      ''}"
                    type="number"
                    step="1"
                    min="0"
                  />
                {/if}
              </td>
            {:else}
              <td
                class="px-2 py-1"
                style={tableStatus[item[primaryKey]]?.[key]
                  ? `background-color: ${tableStatus[item[primaryKey]][key].color}`
                  : ''}
              >
                {#if configVal.editable}
                  <input
                    bind:value={item[key]}
                    on:change={(e) => statusChange(e, item, key)}
                    class="table-input-text"
                    style={configVal.inputStyle}
                  />
                {:else}
                  {item[key] ?? ''}
                {/if}
              </td>
            {/if}
          {/each}
          {#if rowDeletable}
            <td class="text-center px-1 py-0">
              <button
                class="icon-button"
                on:click|stopPropagation={() => rowDelete(item)}
              >
                ✕
              </button>
            </td>
          {/if}
        </tr>
      {/each}
      <slot />
    </tbody>
  </table>
</div>

{#if displayPagination}
  <div
    class="d-flex align-items-center justify-content-end gap-2 mt-1 flex-wrap"
  >
    <div class="d-flex align-items-center gap-1">
      <label class="text-muted small mb-0" for="rows-per-page-select"
        >表示件数</label
      >
      <select
        id="rows-per-page-select"
        class="form-select form-select-sm"
        style="width: auto;"
        bind:value={rowsPerPage}
        on:change={() => {
          currentPage = 0;
          if (serverPagination)
            dispatch('pageChange', { page: 0, rowsPerPage });
        }}
      >
        {#each rowsSelect as n}
          <option value={n}>{n}</option>
        {/each}
      </select>
    </div>
    <span class="text-muted small">
      {totalItems > 0 ? start + 1 : 0}–{end} / {totalItems}件
    </span>
    <div class="btn-group btn-group-sm">
      <button
        class="btn btn-outline-secondary"
        on:click={goFirst}
        disabled={currentPage === 0 && !previousUrl}>«</button
      >
      <button
        class="btn btn-outline-secondary"
        on:click={goPrev}
        disabled={currentPage === 0 && !previousUrl}>‹</button
      >
      <button
        class="btn btn-outline-secondary"
        on:click={goNext}
        disabled={currentPage === lastPage && !nextUrl}>›</button
      >
      <button
        class="btn btn-outline-secondary"
        on:click={goLast}
        disabled={currentPage === lastPage && !nextUrl}>»</button
      >
    </div>
  </div>
{/if}

<style>
  .table-responsive.editable-datatable-container {
    overflow-x: auto;
  }
  .table-responsive.editable-datatable-container::-webkit-scrollbar {
    height: 0.8rem;
  }
  .table-responsive.editable-datatable-container::-webkit-scrollbar-thumb {
    background: #bcbcbc;
  }
  .table-row-clickable {
    cursor: pointer;
  }
  img {
    max-height: 50px;
    padding: 2px;
  }
  .furigana {
    font-size: x-small;
  }
  .table-input-text {
    padding: 0.1rem 0.5rem;
    border: 0.02rem solid #ccc;
    border-radius: 0.25rem;
    width: 100%;
  }
  .table-input-number {
    padding: 0.1rem 0.5rem;
    border: 0.02rem solid #ccc;
    border-radius: 0.25rem;
    text-align: right;
    max-width: 7rem;
  }
  .icon-button {
    background-color: #ddd;
    color: #555;
    border: none;
    border-radius: 1rem;
    padding: 0.1rem 0.4rem;
    font-size: 0.75rem;
    line-height: 1.4;
  }
  .icon-button:hover {
    background-color: #bbb;
  }
  .dropdown-backdrop {
    position: fixed;
    inset: 0;
    z-index: 999;
  }
  .dropdown-menu {
    z-index: 1000;
  }
</style>
